import {isEqual} from 'lodash';
import React, {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {BaseFilterDropdownProps, FilterRow} from './common/types';
import {FilterDropdownSource, FilterRowOf} from './sourceFilter/filterDropdownSource';
import {FilterSourceDropdown} from './sourceFilter/filterSourceDropdown';

/*
 * Props.
 */

export enum ResourceMultiSelectFilterClearBehaviorsEnum {
  /** Set selected items to [] */
  CLEAR_ALL = 'clear_all',

  /** Set selected items to initial selected items */
  CLEAR_NEW_SELECTION = 'clear_new_selection',
}

/*
 * Props.
 */

type ItemId = number;

export type FilterRowGenerator<T> = (
  item: T,
  currentSelectedItemIds: ReadonlyArray<ItemId>,
  makeOnItemSelect: (id: ItemId) => Handler,
  isSearching: boolean,
  initialSelectedItemIds: ReadonlyArray<ItemId>,
) => FilterRow;

export interface ResourceMultiselectFilterDropdownBaseProps<T> {
  dropdownSource: FilterDropdownSource<T>;
  dropdownTitle: ReactNode;
  selectedItemIds: ReadonlyArray<ItemId> | undefined;
  onSetItems: HandlerOf<ReadonlyArray<ItemId>>;
  computeFilterGenerator: FilterRowGenerator<T>;
  onClearAll?: Handler;
  clearBehavior?: ResourceMultiSelectFilterClearBehaviorsEnum;
  applyButtonLabel?: ReactNode;
  clearButtonContent?: ReactNode;
  /** Will auto apply when you select a resource instead of an apply button */
  shouldAutoApply?: boolean;
  searchPlaceholder?: string;
  shouldStayOpenOnClear?: boolean;
}

interface ResourceMultiselectFilterDropdownProps<T>
  extends ResourceMultiselectFilterDropdownBaseProps<T>,
    BaseFilterDropdownProps {}

/*
 * Component.
 */

export const ResourceMultiSelectFilterDropdown = <T,>(props: ResourceMultiselectFilterDropdownProps<T>) => {
  const {
    dropdownTitle,
    dropdownSource,
    computeFilterGenerator,
    selectedItemIds: previousSelectedItemIds,
    onSetItems,
    onClearAll,
    clearBehavior,
    applyButtonLabel,
    clearButtonContent,
    shouldAutoApply,
    searchPlaceholder,
    shouldStayOpenOnClear,
    ...popoverTransferProps
  } = props;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [initialSelectedItemIds] = useState(previousSelectedItemIds);

  const {clearNewSelection, filterResourceWithHandler, showClearButton, isApplyDisabled, onClear, setItems} =
    useResourceMultiSelectFilterDropdownHelpers({
      computeFilterGenerator,
      onSetItems,
      previousSelectedItemIds,
      searchQuery,
      clearBehavior,
      shouldAutoApply,
      initialSelectedItemIds,
    });
  const footerHandlers = !shouldAutoApply
    ? {
        onApplyClicked: setItems,
        onCancelClicked: clearNewSelection,
        applyButtonLabel,
        isApplyDisabled,
      }
    : undefined;

  return (
    <FilterSourceDropdown
      {...popoverTransferProps}
      title={dropdownTitle}
      source={dropdownSource}
      filterResourceOf={filterResourceWithHandler}
      onClear={onClear}
      showClearButton={showClearButton}
      clearButtonContent={clearButtonContent}
      searchProps={{searchQuery, setSearchQuery}}
      onRequestClose={() => {
        if (popoverTransferProps.onRequestClose) {
          popoverTransferProps.onRequestClose();
        }
        clearNewSelection();
      }}
      footerHandlers={footerHandlers}
      searchPlaceholder={searchPlaceholder}
      initialSelectedItemIds={initialSelectedItemIds}
      shouldStayOpenOnClear={shouldStayOpenOnClear}
    />
  );
};

/*
 * Helpers.
 */

interface UseResourceMultiSelectFilterDropdownHelpersProps<T> {
  searchQuery: string;
  previousSelectedItemIds: ReadonlyArray<ItemId> | undefined;
  clearBehavior?: ResourceMultiSelectFilterClearBehaviorsEnum /** @default CLEAR_ALL */;
  computeFilterGenerator: FilterRowGenerator<T>;
  onSetItems: HandlerOf<ReadonlyArray<ItemId>>;
  shouldAutoApply?: boolean;
  initialSelectedItemIds?: ReadonlyArray<ItemId>;
}

export function useResourceMultiSelectFilterDropdownHelpers<T>(
  props: UseResourceMultiSelectFilterDropdownHelpersProps<T>,
) {
  const {
    searchQuery,
    previousSelectedItemIds,
    computeFilterGenerator,
    onSetItems,
    shouldAutoApply,
    initialSelectedItemIds,
  } = props;
  const [currentSelectedItemIds, setCurrentSelectedItemIds] = useState<ReadonlyArray<ItemId>>([]);
  const clearAllBehavior = props.clearBehavior ?? ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_ALL;

  const clearNewSelection = useCallback(() => {
    setCurrentSelectedItemIds(previousSelectedItemIds ?? []);
  }, [previousSelectedItemIds]);

  // If we get new selected id's from the parent clear the selection and reset to the props.
  useEffect(clearNewSelection, [clearNewSelection]);

  const onItemSelect = useCallback(
    (id: ItemId) => () => {
      let results = currentSelectedItemIds;
      if (currentSelectedItemIds.includes(id)) {
        results = currentSelectedItemIds.filter((itemId) => id !== itemId);
      } else {
        results = [...currentSelectedItemIds, id];
      }
      if (shouldAutoApply) {
        onSetItems(results);
      } else {
        setCurrentSelectedItemIds(results);
      }
    },
    [currentSelectedItemIds, onSetItems, shouldAutoApply],
  );

  const filterResourceWithHandler: FilterRowOf<T> = useCallback(
    (item) =>
      computeFilterGenerator(
        item,
        currentSelectedItemIds,
        onItemSelect,
        searchQuery.length > 0,
        initialSelectedItemIds ?? [],
      ),
    [
      computeFilterGenerator,
      currentSelectedItemIds,
      initialSelectedItemIds,
      onItemSelect,
      searchQuery.length,
    ],
  );

  // setItems takes an optional array of new selected ids, if provided it
  // will use them for the to call onSetItems. If not provided, it
  // uses the current value from the state.
  const setItems = useCallback(
    (newSelectedItems?: ReadonlyArray<ItemId>) => {
      const previouslySelectedItems = previousSelectedItemIds ?? [];
      // newSelectedItems can be undefined after a call to onClearAll.
      const selectedItems = newSelectedItems ?? currentSelectedItemIds;

      if (!isEqual(previouslySelectedItems, selectedItems)) {
        onSetItems(selectedItems);
      }
    },
    [currentSelectedItemIds, onSetItems, previousSelectedItemIds],
  );

  const onClear = useCallback(() => {
    switch (clearAllBehavior) {
      case ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_ALL:
        setCurrentSelectedItemIds([]);
        setItems([]);
        return;

      case ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_NEW_SELECTION:
        clearNewSelection();
        return;

      default:
        throw new UnreachableError(clearAllBehavior);
    }
  }, [clearAllBehavior, clearNewSelection, setItems]);

  const hasChanges = useMemo(() => {
    if (previousSelectedItemIds === undefined && currentSelectedItemIds.length === 0) {
      return false;
    }

    return !isEqual(previousSelectedItemIds, currentSelectedItemIds);
  }, [currentSelectedItemIds, previousSelectedItemIds]);

  const showClearButton = useMemo(() => {
    switch (clearAllBehavior) {
      case ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_ALL:
        // Display the clear button if some items were initially selected.
        return (previousSelectedItemIds ?? []).length > 0;

      case ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_NEW_SELECTION: {
        return hasChanges;
      }

      default:
        throw new UnreachableError(clearAllBehavior);
    }
  }, [clearAllBehavior, hasChanges, previousSelectedItemIds]);

  const isApplyEnabled = useMemo(() => {
    switch (clearAllBehavior) {
      case ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_ALL:
        return true;

      case ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_NEW_SELECTION:
        return hasChanges;

      default:
        throw new UnreachableError(clearAllBehavior);
    }
  }, [clearAllBehavior, hasChanges]);

  return {
    showClearButton,
    isApplyDisabled: !isApplyEnabled,
    onClear,
    filterResourceWithHandler,
    clearNewSelection,
    setItems,
  };
}
